import {useDialog} from 'platform/components';
import {Box, Hide, Icon, Show, VStack} from 'platform/foundation';

import {useLocation} from 'react-router-dom';

import {MenuItemProps, UserSidebarIconSize} from '@dms/api/shared';
import {featureFlags} from '@dms/feature-flags';
import {useNavigationBreadCrumb} from '@dms/shared';

import {getSafely, pushGtmEvent, RequiredTestIdProps, suffixTestId} from 'shared/web';

import {SIDEBAR_DESKTOP_ICON_SIZE} from '../constants/sidebarDesktopIconSize';
import {useSidebarMenuItemsOverflow} from '../hooks/useSidebarMenuItemsOverflow';
import {NavigationButton} from './NavigationButton';
import {SidebarCreateEntityShortcut} from './SidebarCreateEntityShortcut';
import {SidebarEditDialog} from './SidebarEditDialog';
import {useSidebarKeyboardControl} from './SidebarKeyboardControlProvider';
import {SidebarLink} from './SidebarLink';
import {SidebarOverflowMenu} from './SidebarOverflowMenu';

interface SidebarNavigationProps extends RequiredTestIdProps {
  createMenuItems: MenuItemProps[];
  menuItemsById: Record<string, MenuItemProps>;
  pinnedMenuItems: string[];
  iconSize: UserSidebarIconSize;
}

export function SidebarNavigation(props: SidebarNavigationProps) {
  const location = useLocation();
  const [breadcrumbProps] = useNavigationBreadCrumb();

  const [isEditDialogOpen, onEditDialogOpen, onEditDialogClose] = useDialog(false);

  const {
    containerRef,
    additionalContentContainerRef,
    visiblePinnedMenuItems,
    overflownPinnedMenuItems,
  } = useSidebarMenuItemsOverflow({
    pinnedMenuItems: props.pinnedMenuItems,
  });

  const {setSidebarItemRef: setItemRef} = useSidebarKeyboardControl();

  return (
    <Box
      ref={containerRef}
      backgroundColor="palettes.neutral.900.100"
      width={12}
      flexGrow={1}
      overflow="hidden"
    >
      <VStack height="100%" align="center" justify="space-between" spacing={1}>
        <Box width="100%">
          {visiblePinnedMenuItems.map((id) => {
            const menuItem = getSafely(props.menuItemsById, id);
            if (!menuItem) {
              return null;
            }

            const isActive = Boolean(
              location.pathname === menuItem.path || breadcrumbProps?.navigateTo === menuItem.path
            );

            return (
              <SidebarLink
                key={menuItem.id}
                ref={(element) => setItemRef(element, id)}
                iconSize={props.iconSize}
                path={menuItem.path}
                icon={menuItem.layoutProps.icon}
                title={menuItem.layoutProps.title}
                action={menuItem.action}
                isActive={isActive}
                data-testid={suffixTestId(`sidebar-link-${menuItem.id}`, props)}
              />
            );
          })}

          <Show when={overflownPinnedMenuItems.length > 0}>
            <SidebarOverflowMenu
              iconSize={props.iconSize}
              pinnedMenuItems={overflownPinnedMenuItems}
              menuItemsById={props.menuItemsById}
            />
          </Show>
        </Box>

        <Box width="100%" ref={additionalContentContainerRef}>
          <Box width="100%">
            <Hide
              when={props.createMenuItems.length === 0}
              whenFeatureEnabled={featureFlags.SIDEBAR_REMOVE_QUICK_ACTIONS}
            >
              <SidebarCreateEntityShortcut
                ref={(element) => setItemRef(element, 'sidebar-create')}
                iconSize={props.iconSize}
                menuItems={props.createMenuItems}
                menuItemsById={props.menuItemsById}
                data-testid={props['data-testid']}
              />
            </Hide>
            <NavigationButton
              ref={(element) => setItemRef(element, 'sidebar-edit')}
              onClick={() => {
                onEditDialogOpen();
                pushGtmEvent({
                  event: 'button_click',
                  event_id: 'sidebar_settings_open',
                  value: {
                    pinnedItems: props.pinnedMenuItems,
                    iconSize: props.iconSize,
                  },
                });
              }}
              isActive={isEditDialogOpen}
            >
              <Icon
                color="text.white"
                size={SIDEBAR_DESKTOP_ICON_SIZE[props.iconSize]}
                value="sidebar/edit"
              />
            </NavigationButton>
            <Show when={isEditDialogOpen}>
              <SidebarEditDialog
                onClose={onEditDialogClose}
                iconSize={props.iconSize}
                menuItemsById={props.menuItemsById}
                pinnedMenuItems={props.pinnedMenuItems}
                data-testid={props['data-testid']}
              />
            </Show>
          </Box>
        </Box>
      </VStack>
    </Box>
  );
}
